import React, { useMemo, useState } from 'react';
import { CSVLink } from 'react-csv';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
  Table,
} from 'reactstrap';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import { useScopedOrders } from '../../hooks/useScopedOrders';
import {
  ARRIVAL_DEPARTURE_COLUMNS,
  SUMMARY_CSV_HEADERS,
  TOUR_COLUMNS,
  buildOperationCsvData,
  buildOperationReportRows,
  buildPassengerCsvData,
  buildPassengerReportRows,
  buildSummaryCsvData,
  buildSummaryReportRows,
  formatReportRangeLabel,
  reportCsvFilename,
  reportExcelFilename,
  splitOperationReportRows,
  type OperationReportRow,
  type PassengerReportRow,
  type ReportKind,
  type SummaryReportRow,
} from '../../utils/reportBuilders';
import { downloadExcelFromAoa } from '../../utils/downloadExcel';
import { toInputDateValue } from '../../utils/opsServices';

const CSVLinkComponent: React.ComponentType<any> = CSVLink as unknown as React.ComponentType<any>;

const emptyCell = (value?: string | number) =>
  value ? <span>{value}</span> : <span className="text-muted">—</span>;

const ReportsHome: React.FC = () => {
  document.title = 'Reports | NGK Tours';

  const today = toInputDateValue();
  const [opFrom, setOpFrom] = useState(today);
  const [opTo, setOpTo] = useState(today);
  const [paxFrom, setPaxFrom] = useState(today);
  const [paxTo, setPaxTo] = useState(today);
  const [summaryFrom, setSummaryFrom] = useState(today);
  const [summaryTo, setSummaryTo] = useState(today);

  const [activeKind, setActiveKind] = useState<ReportKind | null>(null);
  const [activeFrom, setActiveFrom] = useState(today);
  const [activeTo, setActiveTo] = useState(today);
  const [error, setError] = useState<string | null>(null);

  const { orders, loading, error: loadError } = useScopedOrders({
    first: 100,
    paginateAll: true,
  });

  const operationRows = useMemo(() => {
    if (activeKind !== 'operations') return [] as OperationReportRow[];
    return buildOperationReportRows(orders, activeFrom, activeTo);
  }, [orders, activeKind, activeFrom, activeTo]);

  const passengerRows = useMemo(() => {
    if (activeKind !== 'passengers') return [] as PassengerReportRow[];
    return buildPassengerReportRows(orders, activeFrom, activeTo);
  }, [orders, activeKind, activeFrom, activeTo]);

  const summaryRows = useMemo(() => {
    if (activeKind !== 'summary') return [] as SummaryReportRow[];
    return buildSummaryReportRows(orders, activeFrom, activeTo);
  }, [orders, activeKind, activeFrom, activeTo]);

  const operationSections = useMemo(() => splitOperationReportRows(operationRows), [operationRows]);

  const csvData = useMemo(() => {
    if (activeKind === 'operations') return buildOperationCsvData(operationRows, activeFrom, activeTo);
    if (activeKind === 'passengers') return buildPassengerCsvData(passengerRows, activeFrom, activeTo);
    if (activeKind === 'summary') return buildSummaryCsvData(summaryRows, activeFrom, activeTo);
    return [];
  }, [activeKind, operationRows, passengerRows, summaryRows, activeFrom, activeTo]);

  const runReport = (kind: ReportKind, from: string, to: string) => {
    if (!from || !to) {
      setError('Please select both From and To dates.');
      return;
    }
    if (from > to) {
      setError('From date must be on or before To date.');
      return;
    }
    setError(null);
    setActiveKind(kind);
    setActiveFrom(from);
    setActiveTo(to);
  };

  const csvFilename = reportCsvFilename(activeKind || 'operations', activeFrom, activeTo);
  const excelFilename = reportExcelFilename(activeKind || 'operations', activeFrom, activeTo);
  const resultCount =
    activeKind === 'operations'
      ? operationRows.length
      : activeKind === 'passengers'
        ? passengerRows.length
        : activeKind === 'summary'
          ? summaryRows.length
          : 0;

  const reportTitle =
    activeKind === 'operations'
      ? 'Operation List'
      : activeKind === 'passengers'
        ? 'Passengers List'
        : activeKind === 'summary'
          ? 'Summary'
          : '';

  const resultCountLabel =
    activeKind === 'operations'
      ? `${resultCount} row(s)`
      : activeKind === 'passengers'
        ? `${resultCount} passenger(s)`
        : activeKind === 'summary'
          ? `${resultCount} booking(s)`
          : 'Filters applied';

  const renderOperationTable = (
    title: string,
    badgeColor: string,
    columns: readonly string[],
    rows: OperationReportRow[]
  ) => {
    if (!rows.length) return null;
    return (
      <div className="mb-4">
        <div className="d-flex align-items-center gap-2 mb-2">
          <h6 className="mb-0">{title}</h6>
          <Badge color={badgeColor} pill className={badgeColor === 'warning' ? 'text-dark' : undefined}>
            {rows.length}
          </Badge>
        </div>
        <div className="table-responsive">
          <Table className="table-bordered table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                {columns.map((column) => (
                  <th key={column} className="text-nowrap">
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={`${title}-${row['Booking #']}-${row.Date}-${index}`}>
                  {columns.map((column) => {
                    const value = String((row as unknown as Record<string, string>)[column] ?? '');
                    return (
                      <td
                        key={column}
                        className={column === 'Booking #' ? 'fw-medium text-nowrap' : undefined}
                      >
                        {emptyCell(value)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>
    );
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Reports" breadcrumbItem="Reports" />

        <Row className="mb-4">
          <Col md={4} className="mb-3 mb-md-0">
            <Card className="h-100 mb-0">
              <CardHeader className="bg-soft-primary">
                <h5 className="mb-0 text-primary">Operation List</h5>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label className="text-muted">Date From</Label>
                      <Input
                        type="date"
                        value={opFrom}
                        onChange={(e) => setOpFrom(e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label className="text-muted">Date To</Label>
                      <Input type="date" value={opTo} onChange={(e) => setOpTo(e.target.value)} />
                    </FormGroup>
                  </Col>
                </Row>
                <div className="text-center">
                  <Button
                    color="primary"
                    onClick={() => runReport('operations', opFrom, opTo)}
                    disabled={loading}
                  >
                    Show Operation List
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col md={4} className="mb-3 mb-md-0">
            <Card className="h-100 mb-0">
              <CardHeader className="bg-soft-primary">
                <h5 className="mb-0 text-primary">Passengers List</h5>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label className="text-muted">Date From</Label>
                      <Input
                        type="date"
                        value={paxFrom}
                        onChange={(e) => setPaxFrom(e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label className="text-muted">Date To</Label>
                      <Input
                        type="date"
                        value={paxTo}
                        onChange={(e) => setPaxTo(e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <div className="text-center">
                  <Button
                    color="primary"
                    onClick={() => runReport('passengers', paxFrom, paxTo)}
                    disabled={loading}
                  >
                    Show Passenger List
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col md={4}>
            <Card className="h-100 mb-0">
              <CardHeader className="bg-soft-primary">
                <h5 className="mb-0 text-primary">Summary</h5>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label className="text-muted">Date From</Label>
                      <Input
                        type="date"
                        value={summaryFrom}
                        onChange={(e) => setSummaryFrom(e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label className="text-muted">Date To</Label>
                      <Input
                        type="date"
                        value={summaryTo}
                        onChange={(e) => setSummaryTo(e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <div className="text-center">
                  <Button
                    color="primary"
                    onClick={() => runReport('summary', summaryFrom, summaryTo)}
                    disabled={loading}
                  >
                    Show Summary
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {error ? <Alert color="danger">{error}</Alert> : null}
        {loadError ? (
          <Alert color="danger">
            {(loadError as { graphQLErrors?: Array<{ message?: string }> }).graphQLErrors?.[0]
              ?.message || loadError.message}
          </Alert>
        ) : null}

        {loading && !orders.length ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <p className="text-muted mt-2">Loading bookings...</p>
          </div>
        ) : null}

        {activeKind ? (
          <Card>
            <CardBody>
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <div>
                  <h5 className="mb-1">{reportTitle}</h5>
                  <p className="text-muted mb-0 small">
                    {formatReportRangeLabel(activeFrom, activeTo)}
                    {' · '}
                    {resultCountLabel}
                  </p>
                </div>
                {resultCount > 0 ? (
                  <div className="d-flex flex-wrap gap-2">
                    <CSVLinkComponent
                      data={csvData}
                      filename={csvFilename}
                      enclosingCharacter={'"'}
                      uFEFF
                      className="btn btn-primary btn-sm"
                    >
                      Export CSV
                    </CSVLinkComponent>
                    <Button
                      color="success"
                      size="sm"
                      onClick={() =>
                        downloadExcelFromAoa(
                          csvData as Array<Array<string | number>>,
                          excelFilename,
                          reportTitle || 'Report'
                        )
                      }
                    >
                      Export Excel
                    </Button>
                  </div>
                ) : null}
              </div>

              {activeKind === 'summary' ? (
                summaryRows.length === 0 ? (
                  <Alert color="info" className="mb-0">
                    No bookings with service details found for this date range.
                  </Alert>
                ) : (
                  <div className="table-responsive">
                    <Table className="table-bordered table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          {SUMMARY_CSV_HEADERS.map((column) => (
                            <th key={column} className="text-nowrap">
                              {column}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {summaryRows.map((row) => (
                          <tr key={`${row['Booking Code']}-${row['Serial No']}`}>
                            {SUMMARY_CSV_HEADERS.map((column) => {
                              const value = (row as unknown as Record<string, string | number>)[
                                column
                              ];
                              return (
                                <td
                                  key={column}
                                  className={
                                    column === 'Booking Code' ||
                                    column === 'Code' ||
                                    column === 'Serial No'
                                      ? 'fw-medium text-nowrap'
                                      : column === 'Arrival' || column === 'Departure'
                                        ? 'text-nowrap'
                                        : undefined
                                  }
                                >
                                  {emptyCell(value)}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )
              ) : activeKind === 'operations' ? (
                operationRows.length === 0 ? (
                  <Alert color="info" className="mb-0">
                    No bookings found for this date range.
                  </Alert>
                ) : (
                  <>
                    {renderOperationTable(
                      'Arrival / Departure',
                      'info',
                      ARRIVAL_DEPARTURE_COLUMNS,
                      operationSections.arrival
                    )}
                    {renderOperationTable('Tours', 'warning', TOUR_COLUMNS, operationSections.tours)}
                    {renderOperationTable(
                      'Other services',
                      'secondary',
                      ARRIVAL_DEPARTURE_COLUMNS,
                      operationSections.other
                    )}
                  </>
                )
              ) : passengerRows.length === 0 ? (
                <Alert color="info" className="mb-0">
                  No passengers found for bookings in this date range.
                </Alert>
              ) : (
                <div className="table-responsive">
                  <Table className="table-bordered table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>No.</th>
                        <th>Booking #</th>
                        <th>Name</th>
                        <th>Nationality</th>
                        <th>Date of Birth</th>
                        <th>Weight (KG)</th>
                        <th>ID / Passport</th>
                        <th>Gender</th>
                        <th>Mobile</th>
                        <th>Email</th>
                      </tr>
                    </thead>
                    <tbody>
                      {passengerRows.map((row) => (
                        <tr key={`${row['Booking #']}-${row.No}`}>
                          <td>{row.No}</td>
                          <td className="fw-medium text-nowrap">{row['Booking #']}</td>
                          <td>{row.Name}</td>
                          <td>{emptyCell(row.Nationality)}</td>
                          <td className="text-nowrap">{emptyCell(row['Date of Birth'])}</td>
                          <td>{emptyCell(row['Weight (KG)'])}</td>
                          <td>{emptyCell(row['ID / Passport'])}</td>
                          <td>{emptyCell(row.Gender)}</td>
                          <td className="text-nowrap">{emptyCell(row.Mobile)}</td>
                          <td>{emptyCell(row.Email)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        ) : null}
      </Container>
    </div>
  );
};

export default ReportsHome;
